/**
 * Configuración → CAPI Meta.
 * Guía para no-técnicos + embudo 1-2-3-4 (elige etapas del pipeline).
 * El disparo ocurre al mover etapa (core.capi_events). OFF por defecto.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  applyPick, EMBUDO, labelEvento, mapFromPicks, picksFromMap, picksFromSugerido,
} from '@/lib/capiEmbudo';
import {
  Activity, ArrowLeft, ChevronDown, ExternalLink, Eye, EyeOff, Loader2, Save, Sparkles,
} from 'lucide-react';

const META_EVENTS_MANAGER = 'https://business.facebook.com/events_manager2';
const META_CAPI_HELP = 'https://www.facebook.com/business/help/2041148702652965';

function GuiaMeta({ open, onToggle }) {
  return (
    <Card className="bg-card/50 backdrop-blur-md border-cyan-500/20" data-testid="capi-guia">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-6 py-4 flex items-center justify-between gap-3"
        data-testid="capi-guia-toggle"
      >
        <div>
          <p className="text-white text-sm font-semibold">Cómo conectar Meta — guía de 5 minutos</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            No tienes que ser experto. Solo copia dos datos de tu cuenta de anuncios.
          </p>
        </div>
        <ChevronDown className={`w-4 h-4 text-cyan-300 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <CardContent className="pt-0 space-y-4 text-[13px] text-white/80">
          <p className="text-xs text-white/70 leading-relaxed">
            Meta te manda el lead del anuncio. LeadCEM le regresa el <strong className="text-white">avance</strong>:
            si lo contactaste, si agendó, si compró. Así Meta aprende quién sí compra y te manda
            gente parecida — no curiosos. María entra por un anuncio; si termina comprando,
            Meta busca más Marías.
          </p>
          <ol className="space-y-3 list-none">
            {[
              {
                n: '1',
                t: 'Abre el Administrador de eventos',
                d: 'Entra con la cuenta que paga tus anuncios de Facebook o Instagram. Menú → Todas las herramientas → Administrador de eventos.',
              },
              {
                n: '2',
                t: 'Elige tu Pixel y copia el ID',
                d: 'A la izquierda está la lista de Pixels. Elige el mismo que usas en los anuncios que mandan leads a LeadCEM. Arriba aparece un número largo: “ID del conjunto de datos”. Eso es el ID del Pixel. Pégalo abajo.',
              },
              {
                n: '3',
                t: 'Genera el token de acceso',
                d: 'En ese Pixel: Ajustes (engranaje) → baja hasta “API de conversiones” → Generar token de acceso. Cópialo YA: Meta solo lo muestra una vez. Pégalo en Token. LeadCEM lo guarda cifrado; nadie de tu equipo lo vuelve a ver completo.',
              },
              {
                n: '4',
                t: 'Elige 4 etapas de tu pipeline',
                d: 'Abajo, en orden 1-2-3-4: cuál etapa significa “llegó”, “lo contactamos”, “agendó” y “cerró”. No hace falta mapear todas. Con estas cuatro Meta entiende el avance. El vendedor no ve esto.',
              },
              {
                n: '5',
                t: 'Prueba (opcional) y luego activa',
                d: 'En Administrador de eventos, pestaña “Eventos de prueba”, copia el código TEST… Pégalo aquí, guarda, y mueve una oportunidad de prueba. Si ves el evento llegar a Meta, ya quedó. Borra el código de prueba, activa envíos y guarda.',
              },
            ].map((s) => (
              <li key={s.n} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-cyan-400/15 text-cyan-300 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {s.n}
                </span>
                <div>
                  <p className="text-white text-xs font-medium">{s.t}</p>
                  <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap gap-2 pt-1">
            <a
              href={META_EVENTS_MANAGER}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-cyan-300 hover:text-cyan-200 border border-cyan-500/30 rounded-md px-2 py-1"
              data-testid="capi-guia-events-manager"
            >
              Abrir Administrador de eventos <ExternalLink className="w-3 h-3" />
            </a>
            <a
              href={META_CAPI_HELP}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-white/50 hover:text-white/80 border border-white/10 rounded-md px-2 py-1"
            >
              Ayuda de Meta (Conversions API) <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <p className="text-[11px] text-white/40">
            Si el envío a Meta falla, tu vendedor no se entera: la etapa se mueve igual.
          </p>
        </CardContent>
      )}
    </Card>
  );
}

export default function CapiPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [token, setToken] = useState('');
  const [tokenTouched, setTokenTouched] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [guiaOpen, setGuiaOpen] = useState(true);
  const [picks, setPicks] = useState({});

  const load = () => {
    setLoading(true);
    api.get('/capi/config').then((c) => {
      const data = c.data || {};
      setConfig(data);
      const embudo = (data.embudo && data.embudo.length) ? data.embudo : EMBUDO;
      setPicks(picksFromMap(data.stage_map, embudo));
      if (data.has_access_token) setGuiaOpen(false);
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar CAPI');
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setField = (key, value) => setConfig((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        enabled: !!config.enabled,
        pixel_id: config.pixel_id || '',
        test_event_code: config.test_event_code || '',
        stage_map: mapFromPicks(picks),
      };
      if (tokenTouched) body.access_token = token;
      const r = await api.put('/capi/config', body);
      setConfig(r.data);
      const embudo = (r.data.embudo && r.data.embudo.length) ? r.data.embudo : EMBUDO;
      setPicks(picksFromMap(r.data.stage_map, embudo));
      setToken('');
      setTokenTouched(false);
      toast.success('Listo. Cuando un lead avance a una de esas 4 etapas, se lo decimos a Meta.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="capi-loading">
        <Loader2 className="w-6 h-6 text-cyan-300 animate-spin" />
      </div>
    );
  }

  const stages = Array.isArray(config.stages) ? config.stages : [];
  const embudo = (config.embudo && config.embudo.length) ? config.embudo : EMBUDO;
  const sends = config.last_sends || [];
  const used = new Set(Object.values(picks).filter(Boolean));

  const aplicarSugerido = () => {
    const next = picksFromSugerido(config.sugerido, embudo);
    setPicks(next);
    toast.success('Elegimos 4 etapas según los nombres de tu pipeline. Revísalas y guarda.');
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="capi-page">
      <div>
        <Link to="/settings" className="text-xs text-muted-foreground hover:text-white inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Configuración
        </Link>
        <h1 className="font-heading text-2xl font-bold text-white flex items-center gap-2">
          <Activity className="w-6 h-6 text-cyan-400" /> CAPI Meta
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl leading-relaxed">
          Le decimos a Meta cómo avanzan tus leads para que te mande mejores.
          Tú recibes el lead del anuncio; LeadCEM le regresa el avance
          (contacto → cita → venta). Mientras más avanza, más perfilado queda.
        </p>
      </div>

      {config.locked && (
        <p className="text-xs text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded-lg p-3" data-testid="capi-locked">
          Tu plan no incluye CAPI Meta. Pídele a Dealider que lo encienda en el plan.
        </p>
      )}
      {!config.locked && config.plan_capi === false && (
        <p className="text-xs text-amber-200 border border-amber-500/20 bg-amber-500/5 rounded-lg p-3" data-testid="capi-plan-off-hint">
          Puedes configurar, pero no se manda nada a Meta hasta que el plan de esta empresa tenga CAPI ON.
        </p>
      )}

      <GuiaMeta open={guiaOpen} onToggle={() => setGuiaOpen((v) => !v)} />

      <Card className="bg-card/50 backdrop-blur-md border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base">Tus datos de Meta</CardTitle>
          <CardDescription className="text-xs">
            El token se guarda cifrado. Solo verás los últimos 4. Nace apagado: no manda nada hasta que actives.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs text-white/80">Activar envíos a Meta</Label>
              <p className="text-[11px] text-muted-foreground">Apagado = se guarda todo, no se manda nada.</p>
            </div>
            <Switch
              checked={!!config.enabled}
              onCheckedChange={(v) => setField('enabled', v)}
              disabled={!!config.locked}
              data-testid="capi-enabled"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">ID del Pixel</Label>
            <Input
              value={config.pixel_id || ''}
              onChange={(e) => setField('pixel_id', e.target.value)}
              placeholder="El número largo de tu Pixel (paso 2 de la guía)"
              className="bg-black/20 border-white/10 text-xs h-9"
              disabled={!!config.locked}
              data-testid="capi-pixel"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Token de acceso</Label>
              {config.has_access_token && !tokenTouched && (
                <span className="text-[10px] text-emerald-400">Guardado: {config.access_token_preview}</span>
              )}
            </div>
            <div className="relative">
              <Input
                type={showToken ? 'text' : 'password'}
                value={token}
                onChange={(e) => { setToken(e.target.value); setTokenTouched(true); }}
                placeholder={config.has_access_token ? '•••••••• (vacío conserva el actual)' : 'Pega el token (paso 3 de la guía)'}
                className="bg-black/20 border-white/10 text-xs h-9 pr-8"
                disabled={!!config.locked}
                autoComplete="off"
                data-testid="capi-token"
              />
              <button type="button" onClick={() => setShowToken((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                {showToken ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Código de prueba (opcional)</Label>
            <Input
              value={config.test_event_code || ''}
              onChange={(e) => setField('test_event_code', e.target.value)}
              placeholder="TEST12345 — solo para verificar; quítalo cuando ya funcione"
              className="bg-black/20 border-white/10 text-xs h-9"
              disabled={!!config.locked}
              data-testid="capi-test-code"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="capi-embudo">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-white text-base">Qué etapas le contamos a Meta</CardTitle>
              <CardDescription className="text-xs max-w-xl">
                Elige 4 etapas de tu pipeline, en orden. No hace falta ligar todas.
                Vacío en un paso = no se manda nada al llegar a esa etapa.
              </CardDescription>
            </div>
            {stages.length > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={aplicarSugerido}
                disabled={!!config.locked || !config.sugerido || Object.keys(config.sugerido).length === 0}
                className="border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/10 text-[11px] h-8 shrink-0"
                data-testid="capi-sugerir"
              >
                <Sparkles className="w-3 h-3 mr-1.5" />
                Completar con mis etapas
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {stages.length === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="capi-sin-etapas">
              Aún no hay etapas de oportunidades.{' '}
              <Link to="/opportunities" className="text-cyan-300 hover:underline">Créalas en Oportunidades</Link>
              {' '}(el tablero de columnas) y vuelve aquí.
            </p>
          )}
          {embudo.map((step, idx) => (
            <div
              key={step.evento}
              className="flex gap-3 items-start rounded-lg border border-white/5 bg-black/20 p-3"
              data-testid={`capi-paso-${step.paso}`}
            >
              <span className="w-7 h-7 rounded-full bg-[#A5D62C]/15 text-[#A5D62C] text-xs font-bold flex items-center justify-center shrink-0">
                {step.paso}
              </span>
              <div className="flex-1 min-w-0 space-y-2">
                <div>
                  <p className="text-sm text-white font-medium">{step.titulo}</p>
                  <p className="text-[11px] text-muted-foreground">{step.ayuda}</p>
                </div>
                <select
                  value={picks[step.evento] || ''}
                  onChange={(e) => setPicks((prev) => applyPick(prev, step.evento, e.target.value))}
                  disabled={!!config.locked || stages.length === 0}
                  className="h-8 w-full rounded-md bg-black/30 border border-white/10 text-[12px] text-white px-2"
                  data-testid={`capi-map-${step.evento}`}
                >
                  <option value="">— No mandar este paso —</option>
                  {stages.map((st) => {
                    const taken = used.has(st.id) && picks[step.evento] !== st.id;
                    const lost = st.is_lost_stage ? ' (perdido)' : '';
                    return (
                      <option key={st.id} value={st.id} disabled={taken}>
                        {st.name}{lost}{taken ? ' · ya elegida' : ''}
                      </option>
                    );
                  })}
                </select>
                <p className="text-[10px] text-white/35">
                  Meta recibe: “{step.meta_recibe}”
                  {idx < embudo.length - 1 ? ' → el siguiente paso perfila más.' : '.'}
                </p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving || !!config.locked}
          className="bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold text-xs h-9"
          data-testid="capi-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Guardar
        </Button>
      </div>

      <Card className="bg-card/50 backdrop-blur-md border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base">Últimos envíos</CardTitle>
          <CardDescription className="text-xs">Si llegó o no. Sin correos ni token.</CardDescription>
        </CardHeader>
        <CardContent>
          {sends.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="capi-sends-empty">
              Todavía no hay envíos. Mueve una oportunidad a una de las 4 etapas.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="capi-sends">
              {[...sends].reverse().map((s, i) => (
                <li key={s.event_id || i} className="text-[11px] flex items-start justify-between gap-3 border-b border-white/5 pb-2">
                  <div>
                    <span className={s.ok ? 'text-emerald-400' : 'text-red-300'}>{s.ok ? 'OK' : 'Error'}</span>
                    {' · '}
                    <span className="text-white/80">{labelEvento(s.event_name, embudo)}</span>
                    {s.error ? <span className="block text-white/40 mt-0.5">{s.error}</span> : null}
                  </div>
                  <span className="text-white/35 shrink-0">{s.at ? new Date(s.at).toLocaleString('es-MX') : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
